import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { nl } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Thema-datumpicker. value / onChange gebruiken ISO "yyyy-MM-dd" strings (zoals <input type=date>).
// Geef fromYear/toYear mee voor een jaar+maand-dropdown (handig voor geboortedata).
export function DatePicker({
  value,
  onChange,
  testid,
  min,
  placeholder = "Kies een datum",
  className,
  disabled = false,
  fromYear,
  toYear,
}) {
  const [open, setOpen] = useState(false);
  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const valid = parsed && isValid(parsed);
  const minDate = min ? parse(min, "yyyy-MM-dd", new Date()) : undefined;
  const yearNav = fromYear != null && toYear != null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled}
          className={cn(
            "w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none bg-white flex items-center justify-between gap-2 text-left transition-colors hover:bg-brand-violet/5 disabled:opacity-50",
            className
          )}
        >
          <span className={cn("truncate capitalize", valid ? "text-brand-ink" : "text-brand-ink/40")}>
            {valid ? format(parsed, "EEE d MMM yyyy", { locale: nl }) : placeholder}
          </span>
          <CalendarIcon className="w-4 h-4 shrink-0 text-brand-violet" strokeWidth={2.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        data-testid={testid ? `${testid}-popover` : undefined}
        className="w-auto max-w-[calc(100vw-1.5rem)] overflow-x-auto p-0 brutal border-2 border-brand-ink rounded-2xl bg-brand-cream z-[60]"
      >
        <Calendar
          mode="single"
          locale={nl}
          weekStartsOn={1}
          captionLayout={yearNav ? "dropdown-buttons" : "buttons"}
          fromYear={yearNav ? fromYear : undefined}
          toYear={yearNav ? toYear : undefined}
          selected={valid ? parsed : undefined}
          defaultMonth={valid ? parsed : undefined}
          disabled={minDate ? { before: minDate } : undefined}
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          classNames={{
            vhidden: "sr-only",
            caption: "flex flex-col items-center gap-1 pt-1 relative",
            caption_label: "text-sm font-black font-head capitalize",
            caption_dropdowns: "flex gap-2 justify-center",
            dropdown: "brutal-sm rounded-lg bg-white px-1.5 py-1 text-sm font-bold outline-none capitalize",
            dropdown_month: "relative",
            dropdown_year: "relative",
            nav_button: "h-7 w-7 bg-white brutal-sm rounded-lg p-0 hover:bg-brand-violet/10 flex items-center justify-center",
            head_cell: "text-brand-ink/40 rounded-md w-9 font-bold text-[0.7rem] uppercase",
            day: "h-9 w-9 p-0 font-semibold rounded-full hover:bg-brand-violet/15 aria-selected:opacity-100",
            day_selected: "bg-brand-violet text-white hover:bg-brand-violet focus:bg-brand-violet rounded-full",
            day_today: "bg-brand-yellow/60 rounded-full font-black",
            day_outside: "text-brand-ink/30",
            day_disabled: "text-brand-ink/20 opacity-50",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
